import { useParams } from "react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { approveExternalQuote, getExternalQuote, rejectExternalQuote } from "@/lib/external-quote-approval";

export function QuoteApprovalPage() {
  const { token } = useParams<{ token: string }>();
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [done, setDone] = useState<"approved" | "rejected" | null>(null);

  const q = useQuery({
    queryKey: ["external-quote", token],
    queryFn: () => getExternalQuote(token!),
    enabled: !!token,
    retry: false,
  });

  const approve = useMutation({
    mutationFn: () => approveExternalQuote(token!, { externalEmail: email, comment }),
    onSuccess: () => setDone("approved"),
  });
  const reject = useMutation({
    mutationFn: () => rejectExternalQuote(token!, { externalEmail: email, reason: comment || "(no reason)" }),
    onSuccess: () => setDone("rejected"),
  });

  if (q.isLoading) return <div>Loading...</div>;
  if (q.isError) return <div className="text-center text-lg">This link is invalid or expired.</div>;
  const data = q.data!;

  if (done) {
    return <div className="text-center text-lg">Thank you. Your decision has been recorded.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{data.quoteRef}</CardTitle>
        </CardHeader>
        <CardContent>
          <div><strong>Customer:</strong> {data.customerName}</div>
          {data.description && <div><strong>Description:</strong> {data.description}</div>}
          {data.validUntil && <div><strong>Valid until:</strong> {data.validUntil}</div>}
          <div className="text-xl mt-4"><strong>Total:</strong> ${data.totalIncGst.toFixed(2)} (inc GST)</div>
        </CardContent>
      </Card>

      {data.lines.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Line items</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full">
              <thead><tr><th className="text-left">Description</th><th>Qty</th><th>Unit Price</th><th>Line Total</th></tr></thead>
              <tbody>
                {data.lines.map((l, i) => (
                  <tr key={i}><td>{l.description}</td><td>{l.quantity}</td><td>${l.unitPriceExGst.toFixed(2)}</td><td>${l.lineTotalExGst.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {data.inclusions.length > 0 && (
        <Card>
          <CardHeader><CardTitle>What's included</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {data.inclusions.map((inc, i) => (
                <li key={i} className="whitespace-pre-wrap">{inc.bodyText}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {data.termsAndConditionsHtml && (
        <Card>
          <CardHeader><CardTitle>Terms &amp; Conditions</CardTitle></CardHeader>
          <CardContent><div dangerouslySetInnerHTML={{ __html: data.termsAndConditionsHtml }} /></CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Approve or reject</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="email">Your email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="comment">Comment (optional)</Label>
            <Textarea id="comment" value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => approve.mutate()} disabled={!email || approve.isPending || reject.isPending}>Approve</Button>
            <Button variant="destructive" onClick={() => reject.mutate()} disabled={!email || approve.isPending || reject.isPending}>Reject</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
